import { useRef, useMemo, useEffect, memo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Chart,
  LinearScale,
  CategoryScale,
  BarController,
  BarElement,
  Tooltip,
} from 'chart.js';
import { NetIncomeDashboardModel } from 'models';

type Locales = keyof NetIncomeDashboardModel;

const getLabelsAndValues = (
  data: NetIncomeDashboardModel,
  locales: { [key in Locales]: string }
) => {
  const keysOrder: Locales[] = ['GrossIncome', 'CostsIncome', 'NetIncome'];

  return {
    labels: keysOrder.map((key) => locales[key]),
    values: keysOrder.map((key) => data[key]),
  };
};

interface Props {
  dashboardData: NetIncomeDashboardModel;
}

const ChartBarNetIncome: React.FC<Props> = ({ dashboardData }) => {
  const canvas = useRef<HTMLCanvasElement>(null);
  const { t } = useTranslation();

  const locales = useMemo(
    () => ({
      GrossIncome: t('#portal.investor.dashboard.netincomes.grossincome'),
      CostsIncome: t('#portal.investor.dashboard.netincomes.costs'),
      NetIncome: t('#portal.investor.dashboard.netincome.netincome'),
    }),
    [t]
  );

  const renderBarChart = () => {
    if (dashboardData) {
      let context: CanvasRenderingContext2D | null = null;

      if (canvas && canvas.current) {
        context = canvas.current.getContext('2d');
      }

      Chart.register(
        LinearScale,
        CategoryScale,
        BarController,
        BarElement,
        Tooltip
      );

      const { labels, values } = getLabelsAndValues(dashboardData, locales);

      if (context) {
        new Chart(context, {
          type: 'bar',
          data: {
            labels: labels,
            datasets: [
              {
                data: values,
                label: '',
                backgroundColor: [
                  'rgba(255, 99, 132, 0.2)',
                  'rgba(54, 162, 235, 0.2)',
                  'rgba(255, 206, 86, 0.2)',
                ],
                borderColor: [
                  'rgba(255, 99, 132, 1)',
                  'rgba(54, 162, 235, 1)',
                  'rgba(255, 206, 86, 1)',
                ],
                borderWidth: 1,
              },
            ],
          },
          options: {
            plugins: {
              tooltip: {
                callbacks: {
                  label: (context) =>
                    context.parsed.y
                      .toString()
                      .replace(/\./g, ',')
                      .concat(' €'),
                },
              },
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: {
                  callback: (value) => `${value} €`,
                  color: '#224060',
                  font: {
                    family: 'NeoSansStd',
                    size: 13,
                  },
                },
              },
              x: {
                ticks: {
                  color: '#224060',
                  font: {
                    size: 13,
                    family: 'NeoSansStd',
                  },
                },
              },
            },
          },
        });
      }
    }
  };

  useEffect(() => {
    renderBarChart();

    /* eslint-disable-next-line */
  }, []);

  return <canvas ref={canvas}></canvas>;
};

export default memo(ChartBarNetIncome);
